import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { ProcInput, ProcName, ProcOutput } from '@pz/server';
import { rpc, RpcError } from './rpc';

export function useQ<N extends ProcName>(
  name: N,
  input: ProcInput<N>,
  opts: Omit<UseQueryOptions<ProcOutput<N>, RpcError>, 'queryKey' | 'queryFn'> = {},
) {
  return useQuery<ProcOutput<N>, RpcError>({
    queryKey: [name, input],
    queryFn: () => rpc(name, input),
    placeholderData: keepPreviousData,
    ...opts,
  });
}

/** Mutation that invalidates queries afterwards (by procedure name prefix, e.g. 'time.' or 'approvals.'). */
export function useM<N extends ProcName>(name: N, opts: { invalidate?: string[]; onSuccess?: (data: ProcOutput<N>) => void } = {}) {
  const qc = useQueryClient();
  return useMutation<ProcOutput<N>, RpcError, ProcInput<N>>({
    mutationFn: (input) => rpc(name, input),
    onSuccess: async (data) => {
      const prefixes = opts.invalidate ?? [];
      await qc.invalidateQueries({
        predicate: (q) => {
          const key = String(q.queryKey[0]);
          return prefixes.length === 0 || prefixes.some((p) => key.startsWith(p)) || key === 'me.get' || key === 'dashboard.get';
        },
      });
      opts.onSuccess?.(data);
    },
  });
}

/**
 * Tab of a page, kept in the address (`?tab=...`): "back" and "forward" step through tabs like through pages, a tab
 * can be sent as a link and a reload stays on it. `ids` are the tabs the person may see, the first one is the default
 * (it has no parameter in the address). Other parameters of the page (filters, selected project) are kept.
 */
export function useTabParam<T extends string>(ids: readonly T[], key = 'tab'): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value = (raw && (ids as readonly string[]).includes(raw) ? raw : ids[0]) as T;
  const first = ids[0];
  const set = useCallback(
    (v: T) => {
      if (v === value) return;
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (v === first) next.delete(key);
        else next.set(key, v);
        return next;
      });
    },
    [setParams, first, key, value],
  );
  return [value, set];
}

/** The value after it has stayed unchanged for `ms`: a search field asks the server once the person stops typing. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
